import React, { useEffect, useMemo, useState } from 'react';

export default function DateSelector({
  className = "flex w-full flex-col gap-3 sm:flex-row",
  fromMonth,
  setFromMonth,
  fromYear,
  setFromYear,
  toMonth,
  setToMonth,
  toYear,
  setToYear,
}) {
  const [inputType, setInputType] = useState('month');
  const today = new Date();
  const maxYear = today.getFullYear();
  const maxMonth = String(today.getMonth() + 1).padStart(2, '0');
  const maxDay = String(today.getDate()).padStart(2, '0');
  const maxValue = inputType === 'date'
    ? `${maxYear}-${maxMonth}-${maxDay}`
    : `${maxYear}-${maxMonth}`;

  const parseMonthValue = (value) => {
    if (!value) return { year: null, month: null };
    const [year, month] = value.split('-').slice(0, 2).map(Number);
    if (!year || !month) return { year: null, month: null };
    return { year, month };
  };

  const formatMonthYear = (year, month) => {
    const y = Number(year);
    const m = Number(month);
    if (!y || !m) return '';
    const formattedMonth = String(m).padStart(2, '0');
    if (inputType === 'date') {
      return `${y}-${formattedMonth}-01`;
    }
    return `${y}-${formattedMonth}`;
  };

  const fromInputValue = useMemo(
    () => formatMonthYear(fromYear, fromMonth),
    [fromYear, fromMonth, inputType],
  );

  const toInputValue = useMemo(
    () => formatMonthYear(toYear, toMonth),
    [toYear, toMonth, inputType],
  );

  // Firefox does not support a native month picker, so use date picker fallback.
  useEffect(() => {
    const monthInput = document.createElement('input');
    monthInput.setAttribute('type', 'month');
    if (monthInput.type !== 'month') {
      setInputType('date');
    }
  }, []);

  // Validate from-to dates
  useEffect(() => {
    if (fromMonth && fromYear && toMonth && toYear) {
      const fromDate = new Date(Number(fromYear), Number(fromMonth) - 1);
      const toDate = new Date(Number(toYear), Number(toMonth) - 1);
      if (fromDate > toDate) {
        setToMonth(fromMonth);
        setToYear(fromYear);
      }
    }
  }, [fromMonth, fromYear, toMonth, toYear, setToMonth, setToYear]);

  // Handle change for From input
  const handleFromChange = (e) => {
    if (!e.target.value) {
      setFromMonth(null);
      setFromYear(null);
      return;
    }

    const { year, month } = parseMonthValue(e.target.value);
    if (!year || !month) return;

    const selectedDate = new Date(year, month - 1, 1);
    const todayEnd = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    );
    if (selectedDate > todayEnd) return;

    setFromYear(year);
    setFromMonth(month);
  };

  // Handle change for To input
  const handleToChange = (e) => {
    if (!e.target.value) {
      setToMonth(null);
      setToYear(null);
      return;
    }

    const { year, month } = parseMonthValue(e.target.value);
    if (!year || !month) return;

    const selectedDate = new Date(year, month - 1, 1);
    const todayEnd = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    );
    if (selectedDate > todayEnd) return;

    // Prevent selecting To date before From date
    if (fromYear && fromMonth) {
      const fromDate = new Date(Number(fromYear), Number(fromMonth) - 1);
      const toDate = new Date(year, month - 1);
      if (toDate < fromDate) return;
    }

    setToYear(year);
    setToMonth(month);
  };

  return (
    <div className={className}>
      {/* From Date */}
      <div className="flex-1 min-w-[130px] w-full xl:max-w-[180px]">
        <label className="text-sm text-gray-500 font-medium">From</label>
        <input
          type={inputType}
          max={maxValue}
          className="p-2 rounded-md w-full shadow-custom"
          value={fromInputValue}
          onChange={handleFromChange}
          onClick={(e) => e.currentTarget.showPicker?.()}
        />
      </div>
      {/* To Date */}
      <div className="flex-1 min-w-[130px] w-full xl:max-w-[180px]">
        <label className="text-sm text-gray-500 font-medium">To</label>
        <input
          type={inputType}
          min={fromInputValue || undefined}
          max={maxValue}
          className="p-2 rounded-md w-full shadow-custom"
          value={toInputValue}
          onChange={handleToChange}
          onClick={(e) => e.currentTarget.showPicker?.()}
        />
      </div>
    </div>
  );
}
